package link.erpango.quip.catalog.sections

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.Divider
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.QuipDimens
import link.erpango.quip.ui.components.QuipSectionHeader

private data class TypeSample(val name: String, val style: @Composable () -> TextStyle)

@Composable
fun CatalogTypographySection() {
    val samples = listOf(
        TypeSample("h1 · 96sp Light") { MaterialTheme.typography.h1 },
        TypeSample("h2 · 60sp Light") { MaterialTheme.typography.h2 },
        TypeSample("h3 · 48sp Regular") { MaterialTheme.typography.h3 },
        TypeSample("h4 · 34sp Regular") { MaterialTheme.typography.h4 },
        TypeSample("h5 · 24sp Regular") { MaterialTheme.typography.h5 },
        TypeSample("h6 · 20sp Medium") { MaterialTheme.typography.h6 },
        TypeSample("subtitle1 · 16sp Regular") { MaterialTheme.typography.subtitle1 },
        TypeSample("subtitle2 · 14sp Medium") { MaterialTheme.typography.subtitle2 },
        TypeSample("body1 · 16sp Regular") { MaterialTheme.typography.body1 },
        TypeSample("body2 · 14sp Regular") { MaterialTheme.typography.body2 },
        TypeSample("button · 14sp Medium") { MaterialTheme.typography.button },
        TypeSample("caption · 12sp Regular") { MaterialTheme.typography.caption },
        TypeSample("overline · 10sp Regular") { MaterialTheme.typography.overline },
    )

    LazyColumn(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = QuipDimens.padding16, vertical = QuipDimens.padding12),
        verticalArrangement = Arrangement.spacedBy(0.dp),
    ) {
        item {
            QuipSectionHeader(title = "Escala tipográfica")
            Spacer(Modifier.height(4.dp))
            Text(
                text = "La app usa Material Typography. JetBrains Mono está configurado para activar como fuente de display.",
                style = MaterialTheme.typography.caption,
                color = QuipColors.colorTextSecondary,
            )
            Spacer(Modifier.height(QuipDimens.padding12))
        }

        items(samples) { sample ->
            TypeRow(sample = sample)
            Divider(color = QuipColors.colorBackground, thickness = 1.dp)
        }

        item { Spacer(Modifier.height(QuipDimens.padding24)) }

        item {
            QuipSectionHeader(title = "Reglas de uso")
            Spacer(Modifier.height(QuipDimens.padding8))
        }

        item {
            val rules = listOf(
                "Contraste mínimo WCAG AA: 4.5:1 para body, 3:1 para texto grande.",
                "No usar más de 2 familias tipográficas por pantalla.",
                "Line height: 1.4–1.5× el font size para body.",
                "Letter spacing negativo (−0.5) en headlines, positivo (0.25–0.5) en labels en caps.",
            )
            Column(verticalArrangement = Arrangement.spacedBy(QuipDimens.padding8)) {
                rules.forEach { rule ->
                    Row(verticalAlignment = Alignment.Top) {
                        Text("• ", color = QuipColors.colorPrimary, style = MaterialTheme.typography.body2)
                        Text(rule, style = MaterialTheme.typography.body2, color = QuipColors.colorText)
                    }
                }
            }
            Spacer(Modifier.height(QuipDimens.padding24))
        }
    }
}

@Composable
private fun TypeRow(sample: TypeSample) {
    val style = sample.style()
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = QuipDimens.padding8),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = sample.name,
            style = MaterialTheme.typography.caption,
            color = QuipColors.colorTextSecondary,
            modifier = Modifier.weight(0.45f),
        )
        Text(
            text = "The quick brown fox",
            style = style,
            color = QuipColors.colorText,
            modifier = Modifier.weight(0.55f),
            maxLines = 1,
        )
    }
}
